import { EMPTY_INPUT, type InputFrame } from "../shared/types";
import { getControlSettings, subscribeControlSettings } from "./controlSettings";
import {
  getActiveInputDevice,
  type InputDevice,
  markTouchInputActive,
  ON_SCREEN_CONTROL_QUERY,
  prefersOnScreenControls,
  subscribeActiveInputDevice
} from "./inputDevice";

export type TouchControl = "attack" | "jump" | "special";
export type JoystickAxes = Pick<InputFrame, "x" | "y">;

export interface TouchControls {
  read(): InputFrame;
  consumeAttackPress(): boolean;
  destroy(): void;
}

export function joystickAxes(
  deltaX: number,
  deltaY: number,
  radius: number,
  deadZone = 0.22
): JoystickAxes {
  if (radius <= 0) return { x: 0, y: 0 };
  const x = Math.max(-1, Math.min(1, deltaX / radius));
  const y = Math.max(-1, Math.min(1, deltaY / radius));
  if (Math.hypot(x, y) < deadZone) return { x: 0, y: 0 };
  const threshold = 0.36;
  return {
    x: Math.abs(x) >= threshold ? (x < 0 ? -1 : 1) : 0,
    y: Math.abs(y) >= threshold ? (y < 0 ? -1 : 1) : 0
  };
}

export function shouldShowTouchOverlay(options: {
  prefersOnScreen: boolean;
  alwaysShow: boolean;
  device: InputDevice;
}): boolean {
  if (!options.prefersOnScreen) return false;
  if (options.alwaysShow) return true;
  return options.device === "touch";
}

export function paintTouchOverlay(root: ParentNode, visible: boolean): void {
  const container = root.querySelector<HTMLElement>("#touch-controls");
  if (container) {
    container.hidden = !visible;
    container.setAttribute("aria-hidden", String(!visible));
  }
  const documentElement =
    root instanceof Document ? root.documentElement : root.ownerDocument?.documentElement;
  documentElement?.classList.toggle("touch-overlay-on", visible);
  documentElement?.classList.toggle("touch-overlay-off", !visible);
}

export function bindTouchOverlay(root: ParentNode = document, view: Window = window): () => void {
  const query = view.matchMedia?.(ON_SCREEN_CONTROL_QUERY);
  const paint = (): void => {
    paintTouchOverlay(
      root,
      shouldShowTouchOverlay({
        prefersOnScreen: prefersOnScreenControls(query),
        alwaysShow: getControlSettings().alwaysShowTouchControls,
        device: getActiveInputDevice()
      })
    );
  };
  const onScreenPrefersChange = (): void => {
    if (query?.matches && getActiveInputDevice() === "keyboard") {
      markTouchInputActive();
    }
    paint();
  };

  const unsubscribeDevice = subscribeActiveInputDevice(paint);
  const unsubscribeSettings = subscribeControlSettings(paint);
  query?.addEventListener("change", onScreenPrefersChange);
  paint();

  return () => {
    unsubscribeDevice();
    unsubscribeSettings();
    query?.removeEventListener("change", onScreenPrefersChange);
  };
}

export function touchInputFrame(
  active: ReadonlySet<TouchControl>,
  movement: JoystickAxes = { x: 0, y: 0 }
): InputFrame {
  return {
    x: movement.x,
    y: movement.y,
    attack: active.has("attack"),
    jump: active.has("jump"),
    special: active.has("special")
  };
}

export function bindTouchControls(root: ParentNode = document): TouchControls {
  const container = root.querySelector<HTMLElement>("#touch-controls");
  if (!container) {
    return {
      read: () => ({ ...EMPTY_INPUT }),
      consumeAttackPress: () => false,
      destroy: () => undefined
    };
  }

  const pointers = new Map<number, TouchControl>();
  const buttons = new Map<TouchControl, HTMLButtonElement>();
  const buttonCleanups: Array<() => void> = [];
  const joystick = container.querySelector<HTMLButtonElement>("[data-touch-joystick]");
  const joystickThumb = joystick?.querySelector<HTMLElement>(".touch-joystick-thumb");
  let joystickPointer: number | undefined;
  let joystickMovement: JoystickAxes = { x: 0, y: 0 };
  let attackPressed = false;

  const activeControls = (): Set<TouchControl> => new Set(pointers.values());
  const paint = (): void => {
    const active = activeControls();
    for (const [control, button] of buttons) {
      button.classList.toggle("pressed", active.has(control));
      button.setAttribute("aria-pressed", String(active.has(control)));
    }
  };
  const release = (event: PointerEvent): void => {
    if (!pointers.delete(event.pointerId)) return;
    event.preventDefault();
    paint();
  };
  const reset = (): void => {
    pointers.clear();
    joystickPointer = undefined;
    joystickMovement = { x: 0, y: 0 };
    joystick?.classList.remove("pressed");
    joystickThumb?.style.setProperty("--joystick-x", "0px");
    joystickThumb?.style.setProperty("--joystick-y", "0px");
    paint();
  };
  const preventContextMenu = (event: Event): void => event.preventDefault();
  const capturePointer = (element: HTMLElement, pointerId: number): void => {
    try {
      element.setPointerCapture?.(pointerId);
    } catch {
      // Synthetic test events and older browsers may not expose active pointer capture.
    }
  };

  for (const button of container.querySelectorAll<HTMLButtonElement>("[data-touch-control]")) {
    const control = button.dataset.touchControl as TouchControl | undefined;
    if (!control) continue;
    buttons.set(control, button);
    button.setAttribute("aria-pressed", "false");
    const press = (event: PointerEvent): void => {
      event.preventDefault();
      markTouchInputActive();
      pointers.set(event.pointerId, control);
      if (control === "attack") attackPressed = true;
      capturePointer(button, event.pointerId);
      paint();
    };
    button.addEventListener("pointerdown", press);
    button.addEventListener("pointerup", release);
    button.addEventListener("pointercancel", release);
    button.addEventListener("lostpointercapture", release);
    buttonCleanups.push(() => {
      button.removeEventListener("pointerdown", press);
      button.removeEventListener("pointerup", release);
      button.removeEventListener("pointercancel", release);
      button.removeEventListener("lostpointercapture", release);
    });
  }

  const updateJoystick = (event: PointerEvent): void => {
    if (!joystick || event.pointerId !== joystickPointer) return;
    const rect = joystick.getBoundingClientRect();
    const radius = rect.width * 0.32;
    const rawX = event.clientX - (rect.left + rect.width / 2);
    const rawY = event.clientY - (rect.top + rect.height / 2);
    const distance = Math.hypot(rawX, rawY);
    const scale = distance > radius ? radius / distance : 1;
    const x = rawX * scale;
    const y = rawY * scale;
    joystickMovement = joystickAxes(x, y, radius);
    joystickThumb?.style.setProperty("--joystick-x", `${x.toFixed(1)}px`);
    joystickThumb?.style.setProperty("--joystick-y", `${y.toFixed(1)}px`);
  };
  const pressJoystick = (event: PointerEvent): void => {
    if (!joystick || joystickPointer !== undefined) return;
    event.preventDefault();
    markTouchInputActive();
    joystickPointer = event.pointerId;
    joystick.classList.add("pressed");
    capturePointer(joystick, event.pointerId);
    updateJoystick(event);
  };
  const releaseJoystick = (event: PointerEvent): void => {
    if (event.pointerId !== joystickPointer) return;
    event.preventDefault();
    joystickPointer = undefined;
    joystickMovement = { x: 0, y: 0 };
    joystick?.classList.remove("pressed");
    joystickThumb?.style.setProperty("--joystick-x", "0px");
    joystickThumb?.style.setProperty("--joystick-y", "0px");
  };
  joystick?.addEventListener("pointerdown", pressJoystick);
  joystick?.addEventListener("pointermove", updateJoystick);
  joystick?.addEventListener("pointerup", releaseJoystick);
  joystick?.addEventListener("pointercancel", releaseJoystick);
  joystick?.addEventListener("lostpointercapture", releaseJoystick);

  container.addEventListener("contextmenu", preventContextMenu);
  window.addEventListener("blur", reset);
  document.addEventListener("visibilitychange", reset);

  return {
    read: () => touchInputFrame(activeControls(), joystickMovement),
    consumeAttackPress: () => {
      const pressed = attackPressed;
      attackPressed = false;
      return pressed;
    },
    destroy: () => {
      reset();
      for (const cleanup of buttonCleanups) cleanup();
      joystick?.removeEventListener("pointerdown", pressJoystick);
      joystick?.removeEventListener("pointermove", updateJoystick);
      joystick?.removeEventListener("pointerup", releaseJoystick);
      joystick?.removeEventListener("pointercancel", releaseJoystick);
      joystick?.removeEventListener("lostpointercapture", releaseJoystick);
      container.removeEventListener("contextmenu", preventContextMenu);
      window.removeEventListener("blur", reset);
      document.removeEventListener("visibilitychange", reset);
    }
  };
}
